<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: null,
                    },
                    {
                        name: $t('messages.create'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.store" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6 md:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.title')" for="title" />
                            <FormInput
                                id="title"
                                v-model="form.title"
                                type="text"
                                name="title"
                                :placeholder="$t('messages.(optional)')"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.title" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <FormLabel :value="$t('messages.grade_level')" for="grade_level_ids" />
                        <div class="mt-1">
                            <label v-for="grade_level in options.grade_levels" class="mr-1" :key="grade_level.value">
                                <input
                                    v-model="form.grade_level_ids"
                                    name="grade_levels"
                                    :value="grade_level.value"
                                    type="checkbox"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                {{ grade_level.text }}
                            </label>
                        </div>
                        <FormError :value="form.errors.grade_level_ids" class="mt-2" />
                    </div>

                    <div class="sm:col-span-8">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.base')" for="title" />
                            <FormInput
                                required
                                id="base"
                                v-model.number="form.base"
                                name="base"
                                type="number"
                                step="1"
                                min="5"
                                max="1000"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.base" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.ratio')" for="trimester" />
                            <FormInput
                                required
                                id="title"
                                v-model.number="form.ratio"
                                name="ratio"
                                type="number"
                                step="0.01"
                                min="1.1"
                                max="2.0"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.ratio" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.health_max')" for="health_max" />
                            <FormInput
                                required
                                id="health_max"
                                v-model.number="form.health_max"
                                name="health_max"
                                type="number"
                                step="1"
                                min="10"
                                max="10000"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.health_max" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="flex flex-col mt-5">
                            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                                <div
                                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                                >
                                    <table class="min-w-full divide-y divide-gray-200">
                                        <thead>
                                            <tr>
                                                <th
                                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                                >
                                                    {{ $tc('messages.ranks', 1) }}
                                                </th>
                                                <th
                                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                                >
                                                    {{ $tc('messages.levels', 2) }}
                                                </th>
                                                <th
                                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                                >
                                                    <span class="sr-only">{{ $tc('messages.actions', 1) }}</span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                :class="rankIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                                v-for="(rank, rankIndex) in form.ranks"
                                                :key="rankIndex"
                                            >
                                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                                    <FormInput
                                                        required
                                                        :id="`name_${rankIndex}`"
                                                        v-model.trim="rank.name"
                                                        name="name"
                                                        type="text"
                                                        step="1"
                                                        class="mt-1 w-full"
                                                    />
                                                </td>
                                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                                    <FormInput
                                                        required
                                                        :id="`number_of_levels_${rankIndex}`"
                                                        v-model.number="rank.number_of_levels"
                                                        name="number_of_levels"
                                                        type="number"
                                                        step="1"
                                                        min="1"
                                                        max="50"
                                                        class="mt-1 w-full"
                                                    />
                                                </td>
                                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                                    <button
                                                        v-if="rankIndex !== 0"
                                                        type="button"
                                                        @click="deleteRank(rankIndex)"
                                                        class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                    >
                                                        <svg
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                            class="trash w-4 h-4"
                                                        >
                                                            <path
                                                                fill-rule="evenodd"
                                                                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                                clip-rule="evenodd"
                                                            ></path>
                                                        </svg>
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <PlusButton @click="addRank" class="mt-5" />
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection v-if="hasValidParameters">
                <template v-slot:title>
                    {{ $tc('messages.levels', 2) }}
                </template>
                <div class="flex flex-col mt-5">
                    <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                        <div
                            class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                        >
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr>
                                        <th
                                            class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $tc('messages.numbers', 1) }}
                                        </th>
                                        <th
                                            class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $tc('messages.experience_points', 2) }}
                                        </th>
                                        <th
                                            class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $tc('messages.ranks', 1) }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                        v-for="(level, index) in generatedLevels"
                                        :key="`generatedLevels_${index}`"
                                    >
                                        <td
                                            class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            {{ index }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                            {{ level.width }} [{{ level.min }} - {{ level.max }}]
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                            {{ level.rank }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <div class="w-full mt-5 flex justify-end items-center">
                <div>
                    <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
                </div>
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import PlusButton from '@/components/Forms/PlusButton.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

function generateGamificationLevelRanges($base, $ratio, $ranks) {
    const levels = [];

    let newBase = $base;
    let min = 0;
    let max = $base;

    $ranks.forEach((rank) => {
        for (let i = 1; i <= rank.number_of_levels; i++) {
            let level = {
                rank: rank.name,
                width: newBase,
                min: min,
                max: max,
            };
            newBase = Math.floor(newBase * $ratio);
            min = max + 1;
            max += newBase;

            levels.push(level);
        }
    });

    return levels;
}

export default {
    components: {
        SubmitFormButton,
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        PlusButton,
    },
    props: ['meta', 'options', 'defaults'],
    layout: Layout,
    data() {
        return {
            numberOfLevels: 1,
            form: this.$inertia.form({
                title: '',
                base: 50,
                ratio: 1.2,
                health_max: 100,
                grade_level_ids: [],
                ranks: this.defaults.ranks,
            }),
        };
    },
    name: 'Create',
    computed: {
        generatedLevels: function () {
            if (this.hasValidParameters === false) {
                return [];
            }
            return generateGamificationLevelRanges(this.form.base, this.form.ratio, this.form.ranks);
        },
        hasValidParameters() {
            if (this.form.ratio < 1.1) {
                return false;
            }
            if (this.form.ratio > 2) {
                return false;
            }
            if (this.form.base < 5) {
                return false;
            }
            if (this.form.base > 1000) {
                return false;
            }

            return true;
        },
    },
    methods: {
        deleteRank(index) {
            this.form.ranks.splice(index, 1);
        },
        addRank() {
            this.form.ranks.push({
                name: '',
                number_of_levels: 1,
            });
        },
        submit() {
            this.form.post(this.meta.links.store);
        },
    },
    mounted() {},
};
</script>
